import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  FileText,
  Plus,
  Search,
  Eye,
  XCircle,
  CheckCircle,
  Car,
  User,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  ArrowUpDown,
  Printer,
  FileDown
} from 'lucide-react';

const statusColors = {
  ativo: 'bg-green-500',
  encerrado: 'bg-blue-500',
  cancelado: 'bg-red-500'
};

const statusLabels = {
  ativo: 'Ativo',
  encerrado: 'Encerrado',
  cancelado: 'Cancelado'
};

export default function ContractsPage() {
  const { API } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ativo'); // Padrão: mostrar apenas ativos
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('data_final'); // Padrão: ordenar por data de vencimento
  const [showInactive, setShowInactive] = useState(false); // Toggle para mostrar cancelados/inativos
  
  // Search state for client selector
  const [clientSearch, setClientSearch] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showGenerateDocModal, setShowGenerateDocModal] = useState(false);
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [generatedDocument, setGeneratedDocument] = useState('');
  const [documentEditorState, setDocumentEditorState] = useState(EditorState.createEmpty());
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    vehicle_id: '',
    tipo_cobranca: 'mensal',
    valor_parcela: 0,
    duracao_meses: 12,
    multa_diaria: 0,
    data_inicio: new Date().toISOString().split('T')[0],
    gerar_parcelas: true,
    primeira_parcela_inicio: true
  });
  
  // Add invoice form state
  const [invoiceFormData, setInvoiceFormData] = useState({
    descricao: '',
    vencimento: new Date().toISOString().split('T')[0],
    valor_original: 0,
    observacoes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadContracts();
    loadClients();
    loadVehicles();
    loadTemplates();
  }, [statusFilter]);

  const loadContracts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'todos') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/contracts?${params}`, { withCredentials: true });
      setContracts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      // Carregar TODOS os clientes (sem filtro de status)
      const response = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?status=disponivel`, { withCredentials: true });
      const reservedResponse = await axios.get(`${API}/vehicles?status=reservado`, { withCredentials: true });
      setVehicles([...response.data, ...reservedResponse.data]);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates?status=ativo`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const openGenerateDocModal = (contract) => {
    setSelectedContract(contract);
    setSelectedTemplateId('');
    setGeneratedDocument('');
    setShowGenerateDocModal(true);
  };

  const generateDocument = async () => {
    if (!selectedTemplateId) {
      toast.error('Selecione um modelo de documento');
      return;
    }

    try {
      const response = await axios.post(`${API}/documents/generate`, {
        template_id: selectedTemplateId,
        contract_id: selectedContract.contract_id
      }, { withCredentials: true });
      
      const htmlContent = response.data.content;
      setGeneratedDocument(htmlContent);
      
      // Convert HTML to EditorState for editing
      try {
        const blocksFromHTML = convertFromHTML(htmlContent);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        setDocumentEditorState(EditorState.createWithContent(contentState));
      } catch (error) {
        console.error('Error converting HTML:', error);
        setDocumentEditorState(EditorState.createEmpty());
      }
      
      setIsEditingDocument(false);
      toast.success('Documento gerado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar documento');
    }
  };

  const saveEditedDocument = () => {
    const editedHTML = draftToHtml(convertToRaw(documentEditorState.getCurrentContent()));
    setGeneratedDocument(editedHTML);
    setIsEditingDocument(false);
    toast.success('Edições salvas!');
  };

  const printDocument = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Documento - ${selectedContract?.contract_id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
          h1, h2, h3, h4, h5, h6 { margin-top: 20px; margin-bottom: 10px; }
          p { margin: 10px 0; }
          ul, ol { margin: 10px 0; padding-left: 30px; }
        </style>
      </head>
      <body>
        ${generatedDocument}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contracts`, formData, { withCredentials: true });
      toast.success('Contrato criado com sucesso!');
      setShowModal(false);
      resetForm();
      loadContracts();
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar contrato');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndContract = async () => {
    try {
      await axios.put(`${API}/contracts/${selectedContract.contract_id}`, { status: 'encerrado' }, { withCredentials: true });
      toast.success('Contrato encerrado com sucesso!');
      setShowEndDialog(false);
      loadContracts();
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao encerrar contrato');
    }
  };

  const handleCancelContract = async () => {
    try {
      await axios.put(`${API}/contracts/${selectedContract.contract_id}`, { status: 'cancelado' }, { withCredentials: true });
      toast.success('Contrato cancelado com sucesso!');
      setShowCancelDialog(false);
      loadContracts();
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao cancelar contrato');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      vehicle_id: '',
      tipo_cobranca: 'mensal',
      valor_parcela: 0,
      duracao_meses: 12,
      multa_diaria: 0,
      data_inicio: new Date().toISOString().split('T')[0],
      gerar_parcelas: true,
      primeira_parcela_inicio: true
    });
    setClientSearch(''); // Limpar busca de cliente
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openViewModal = (contract) => {
    setSelectedContract(contract);
    setShowViewModal(true);
  };

  const openEndDialog = (contract) => {
    setSelectedContract(contract);
    setShowEndDialog(true);
  };

  const openCancelDialog = (contract) => {
    setSelectedContract(contract);
    setShowCancelDialog(true);
  };

  const openAddInvoiceModal = (contract) => {
    setSelectedContract(contract);
    setInvoiceFormData({
      descricao: '',
      vencimento: new Date().toISOString().split('T')[0],
      valor_original: 0,
      observacoes: ''
    });
    setShowAddInvoiceModal(true);
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/invoices`, {
        contract_id: selectedContract.contract_id,
        vencimento: invoiceFormData.vencimento,
        valor_original: parseFloat(invoiceFormData.valor_original),
        multa: 0,
        observacoes: invoiceFormData.observacoes || '',
        nome_parcela: invoiceFormData.descricao || 'Parcela Avulsa'  // Send the name for avulsa parcel
      }, { withCredentials: true });

      toast.success('Parcela avulsa adicionada com sucesso!');
      setShowAddInvoiceModal(false);
      loadContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar parcela');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  // Calculate days until contract ends
  const getDaysUntilEnd = (dataFinal) => {
    if (!dataFinal) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(dataFinal + 'T00:00:00');
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check if contract is ending soon (within 10 days)
  const isEndingSoon = (dataFinal) => {
    const days = getDaysUntilEnd(dataFinal);
    return days !== null && days >= 0 && days <= 10;
  };

  // Calculate estimated values
  const calculateTotals = () => {
    let numParcelas = 0;
    if (formData.tipo_cobranca === 'semanal') {
      numParcelas = formData.duracao_meses * 4;
    } else if (formData.tipo_cobranca === 'quinzenal') {
      numParcelas = formData.duracao_meses * 2;
    } else {
      numParcelas = formData.duracao_meses;
    }
    const valorTotal = formData.valor_parcela * numParcelas;
    return { numParcelas, valorTotal };
  };

  const { numParcelas, valorTotal } = calculateTotals();

  // Get filtered and sorted contracts
  const getFilteredContracts = () => {
    let filtered = contracts;
    
    // Filtro por status (se não estiver mostrando inativos, ocultar cancelados e encerrados)
    if (!showInactive) {
      filtered = filtered.filter(c => c.status === 'ativo');
    } else if (statusFilter !== 'todos') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    
    // Filtro por busca
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(c => 
        c.client_name?.toLowerCase().includes(s) || 
        c.vehicle_info?.toLowerCase().includes(s) ||
        c.vehicle_id?.toLowerCase().includes(s)
      );
    }

    // Ordenar contratos
    filtered.sort((a, b) => {
      if (sortBy === 'vehicle_id') {
        return (a.vehicle_id || '').localeCompare(b.vehicle_id || '');
      } else if (sortBy === 'data_final') {
        // Ordenar por data de vencimento (mais próximos primeiro)
        return new Date(a.data_final) - new Date(b.data_final);
      } else {
        return new Date(b.data_inicio) - new Date(a.data_inicio);
      }
    });

    return filtered;
  };

  const filteredContracts = getFilteredContracts();

  // Count contracts ending soon
  const endingSoonCount = contracts.filter(c => c.status === 'ativo' && isEndingSoon(c.data_final)).length;

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Contratos</h1>
          <p className="text-gray-500 text-sm">Gerencie os contratos de locação</p>
        </div>
        <Button onClick={openCreateModal} data-testid="add-contract-button">
          <Plus className="w-4 h-4 mr-2" />
          Novo Contrato
        </Button>
      </div>

      {/* Alert for contracts ending soon */}
      {endingSoonCount > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 font-medium">
              {endingSoonCount} contrato{endingSoonCount > 1 ? 's' : ''} com prazo próximo de vencimento (10 dias ou menos)
            </span>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por cliente, veículo ou ID do veículo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-contracts"
              />
            </div>
            
            {/* Filtro de Status (só aparece quando "mostrar inativos" está ativo) */}
            {showInactive && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Status</SelectItem>
                  <SelectItem value="ativo">Ativos</SelectItem>
                  <SelectItem value="encerrado">Encerrados</SelectItem>
                  <SelectItem value="cancelado">Cancelados</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px]" data-testid="sort-filter">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="data_final">Vencimento (próximos)</SelectItem>
                <SelectItem value="data_inicio">Data de Início</SelectItem>
                <SelectItem value="vehicle_id">ID do Veículo</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Toggle para mostrar inativos */}
            <Button
              variant={showInactive ? "default" : "outline"}
              onClick={() => {
                setShowInactive(!showInactive);
                if (!showInactive) {
                  setStatusFilter('todos');
                } else {
                  setStatusFilter('ativo');
                }
              }}
              className={showInactive ? "bg-orange-600 hover:bg-orange-700" : ""}
              data-testid="toggle-inactive"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {showInactive ? 'Ocultar Inativos' : 'Mostrar Inativos'}
            </Button>
          </div>

          {/* Indicador de filtro ativo */}
          {!showInactive && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-2">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              <span>Exibindo apenas contratos <strong>ativos</strong>, ordenados por vencimento próximo</span>
            </div>
          )}
          
          {showInactive && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 bg-orange-50 border border-orange-200 rounded-lg p-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <span>Exibindo contratos <strong>inativos</strong> (encerrados e cancelados)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contract Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredContracts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FileText className="w-12 h-12 mb-2 opacity-50" />
            <p>Nenhum contrato encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContracts.map((contract) => {
            const daysUntilEnd = getDaysUntilEnd(contract.data_final);
            const endingSoon = contract.status === 'ativo' && isEndingSoon(contract.data_final);
            
            return (
              <Card 
                key={contract.contract_id} 
                className={`contract-card overflow-hidden hover:shadow-lg transition-shadow ${endingSoon ? 'ring-2 ring-amber-400' : ''}`}
                data-testid={`contract-card-${contract.contract_id}`}
              >
                {/* Status Banner */}
                <div className={`h-2 ${statusColors[contract.status]}`}></div>
                
                <CardContent className="p-4">
                  {/* Header with Vehicle ID and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-mono text-lg font-bold text-blue-600">{contract.vehicle_id}</p>
                      <p className="text-sm text-gray-500">{contract.vehicle_info}</p>
                    </div>
                    <Badge className={`${statusColors[contract.status]} text-white`}>
                      {statusLabels[contract.status]}
                    </Badge>
                  </div>

                  {/* Ending Soon Alert */}
                  {endingSoon && (
                    <div className="mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-800 font-medium">
                        {daysUntilEnd === 0 ? 'Vence hoje!' : `Vence em ${daysUntilEnd} dia${daysUntilEnd > 1 ? 's' : ''}`}
                      </span>
                    </div>
                  )}

                  {/* Contract Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{contract.client_name}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-gray-500">Início</p>
                        <p className="font-medium">{formatDate(contract.data_inicio)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Fim</p>
                        <p className="font-medium">{formatDate(contract.data_final)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Parcela</p>
                        <p className="font-semibold text-green-600">{formatCurrency(contract.valor_parcela)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total Contrato</p>
                        <p className="font-semibold text-blue-600">{formatCurrency(contract.valor_total || contract.valor_parcela * (contract.num_parcelas || 1))}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Cobrança</p>
                        <p className="font-medium capitalize">{contract.tipo_cobranca}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t">
                    {contract.status === 'ativo' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAddInvoiceModal(contract)}
                        className="text-green-600 hover:text-green-700"
                        title="Adicionar Parcela Avulsa"
                        data-testid={`add-invoice-${contract.contract_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Parcela
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openGenerateDocModal(contract)}
                      title="Gerar Documento"
                      data-testid={`generate-doc-${contract.contract_id}`}
                    >
                      <FileDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openViewModal(contract)}
                      data-testid={`view-contract-${contract.contract_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {contract.status === 'ativo' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEndDialog(contract)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Encerrar"
                          data-testid={`end-contract-${contract.contract_id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCancelDialog(contract)}
                          className="text-red-600 hover:text-red-700"
                          title="Cancelar"
                          data-testid={`cancel-contract-${contract.contract_id}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Generate Document Modal */}
      <Dialog open={showGenerateDocModal} onOpenChange={setShowGenerateDocModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Gerar Documento - Contrato {selectedContract?.contract_id}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Template Selection */}
            <div>
              <Label>Selecione o Modelo de Documento</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger data-testid="select-template">
                  <SelectValue placeholder="Escolha um modelo..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.nome} ({t.tipo})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Generate Button */}
            {!generatedDocument && (
              <Button 
                onClick={generateDocument} 
                className="w-full"
                disabled={!selectedTemplateId}
                data-testid="generate-doc-button"
              >
                <FileText className="w-4 h-4 mr-2" />
                Gerar Documento
              </Button>
            )}

            {/* Generated Document Preview */}
            {generatedDocument && (
              <>
                {!isEditingDocument ? (
                  <div className="border rounded-lg p-6 bg-white max-h-[55vh] overflow-y-auto">
                    <div 
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: generatedDocument }}
                    />
                  </div>
                ) : (
                  <div className="border rounded-lg bg-white">
                    {/* Line Height Selector for Document */}
                    <div className="flex items-center gap-2 mb-3 text-sm bg-gray-50 p-2 rounded-lg">
                      <span className="text-gray-600">Espaçamento entre linhas:</span>
                      <select 
                        className="border rounded px-2 py-1 text-sm"
                        onChange={(e) => {
                          const lineHeight = e.target.value;
                          const editor = document.querySelector('.demo-editor-doc');
                          if (editor) editor.style.lineHeight = lineHeight;
                        }}
                        defaultValue="1.6"
                      >
                        <option value="1.0">Simples (1.0)</option>
                        <option value="1.15">1.15</option>
                        <option value="1.5">1.5</option>
                        <option value="1.6">1.6</option>
                        <option value="2.0">Duplo (2.0)</option>
                        <option value="2.5">2.5</option>
                        <option value="3.0">Triplo (3.0)</option>
                      </select>
                    </div>
                    
                    <Editor
                      editorState={documentEditorState}
                      onEditorStateChange={setDocumentEditorState}
                      wrapperClassName="demo-wrapper-doc"
                      editorClassName="demo-editor-doc px-4"
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'remove', 'history'],
                        inline: { 
                          inDropdown: false,
                          options: ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript'],
                        },
                        blockType: {
                          inDropdown: true,
                          options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
                          className: 'rdw-block-dropdown',
                        },
                        fontSize: {
                          options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72],
                          className: 'rdw-fontsize-dropdown',
                        },
                        fontFamily: {
                          options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana', 'Courier New'],
                          className: 'rdw-fontfamily-dropdown',
                        },
                        list: { 
                          inDropdown: false,
                          options: ['unordered', 'ordered', 'indent', 'outdent'] 
                        },
                        textAlign: { 
                          inDropdown: false,
                          options: ['left', 'center', 'right', 'justify'] 
                        },
                        colorPicker: {
                          colors: ['rgb(0,0,0)', 'rgb(68,68,68)', 'rgb(102,102,102)', 'rgb(153,153,153)', 
                                   'rgb(204,0,0)', 'rgb(230,145,56)', 'rgb(255,255,0)', 'rgb(0,128,0)',
                                   'rgb(0,0,255)', 'rgb(102,0,204)', 'rgb(255,0,255)', 'rgb(255,255,255)'],
                        },
                        history: {
                          inDropdown: false,
                          options: ['undo', 'redo'],
                        },
                      }}
                      localization={{
                        locale: 'pt',
                        translations: {
                          'generic.add': 'Adicionar',
                          'generic.cancel': 'Cancelar',
                          'components.controls.blocktype.normal': 'Normal',
                          'components.controls.blocktype.h1': 'Título 1',
                          'components.controls.blocktype.h2': 'Título 2',
                          'components.controls.blocktype.h3': 'Título 3',
                          'components.controls.blocktype.h4': 'Título 4',
                          'components.controls.blocktype.h5': 'Título 5',
                          'components.controls.blocktype.h6': 'Título 6',
                          'components.controls.blocktype.blockquote': 'Citação',
                          'components.controls.fontsize.fontsize': 'Tamanho',
                          'components.controls.fontfamily.fontfamily': 'Fonte',
                        }
                      }}
                    />
                  </div>
                )}

                <div className="flex justify-between gap-2">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setGeneratedDocument('');
                        setSelectedTemplateId('');
                        setIsEditingDocument(false);
                      }}
                    >
                      Gerar Outro
                    </Button>
                    {!isEditingDocument ? (
                      <Button 
                        variant="outline" 
                        onClick={() => setIsEditingDocument(true)}
                      >
                        ✏️ Editar Documento
                      </Button>
                    ) : (
                      <>
                        <Button 
                          onClick={saveEditedDocument}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          ✓ Salvar Edições
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => setIsEditingDocument(false)}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {!isEditingDocument && (
                    <Button onClick={printDocument} className="bg-blue-600 hover:bg-blue-700">
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir / Salvar PDF
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDocModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Contrato</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Selection */}
              <div className="md:col-span-2">
                <Label htmlFor="client_id">Cliente *</Label>
                {/* Search input for clients */}
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar cliente por nome..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Select 
                  value={formData.client_id || "placeholder"} 
                  onValueChange={(value) => setFormData({ ...formData, client_id: value === "placeholder" ? '' : value })}
                >
                  <SelectTrigger data-testid="contract-client-select">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>Selecione o cliente</SelectItem>
                    {clients
                      .filter(client => 
                        clientSearch === '' || 
                        client.nome.toLowerCase().includes(clientSearch.toLowerCase()) ||
                        client.cpf_cnpj.includes(clientSearch)
                      )
                      .map((client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.nome} - {client.cpf_cnpj}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Selection */}
              <div className="md:col-span-2">
                <Label htmlFor="vehicle_id">Veículo *</Label>
                <Select 
                  value={formData.vehicle_id || "placeholder"} 
                  onValueChange={(value) => setFormData({ ...formData, vehicle_id: value === "placeholder" ? '' : value })}
                >
                  <SelectTrigger data-testid="contract-vehicle-select">
                    <SelectValue placeholder="Selecione o veículo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>Selecione o veículo</SelectItem>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                        {vehicle.vehicle_id} - {vehicle.marca} {vehicle.modelo} - {vehicle.placa} ({vehicle.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Billing Config */}
              <div>
                <Label htmlFor="tipo_cobranca">Tipo de Cobrança *</Label>
                <Select 
                  value={formData.tipo_cobranca} 
                  onValueChange={(value) => setFormData({ ...formData, tipo_cobranca: value })}
                >
                  <SelectTrigger data-testid="contract-billing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semanal">Semanal (7 dias)</SelectItem>
                    <SelectItem value="quinzenal">Quinzenal (14 dias)</SelectItem>
                    <SelectItem value="mensal">Mensal (30 dias)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="valor_parcela">Valor da Parcela (R$) *</Label>
                <Input
                  id="valor_parcela"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor_parcela}
                  onChange={(e) => setFormData({ ...formData, valor_parcela: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="contract-valor-input"
                />
              </div>

              <div>
                <Label htmlFor="duracao_meses">Duração (meses) *</Label>
                <Input
                  id="duracao_meses"
                  type="number"
                  min="1"
                  value={formData.duracao_meses}
                  onChange={(e) => setFormData({ ...formData, duracao_meses: parseInt(e.target.value) || 1 })}
                  required
                  data-testid="contract-duracao-input"
                />
              </div>

              <div>
                <Label htmlFor="multa_diaria">Multa Diária por Atraso (R$)</Label>
                <Input
                  id="multa_diaria"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.multa_diaria}
                  onChange={(e) => setFormData({ ...formData, multa_diaria: parseFloat(e.target.value) || 0 })}
                  data-testid="contract-multa-input"
                />
              </div>

              <div>
                <Label htmlFor="data_inicio">Data de Início *</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                  required
                  data-testid="contract-data-input"
                />
              </div>

              {/* Options */}
              <div className="md:col-span-2 space-y-3 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="gerar_parcelas" 
                    checked={formData.gerar_parcelas}
                    onCheckedChange={(checked) => setFormData({ ...formData, gerar_parcelas: checked })}
                    data-testid="contract-gerar-check"
                  />
                  <Label htmlFor="gerar_parcelas" className="font-normal cursor-pointer">
                    Gerar parcelas automaticamente
                  </Label>
                </div>
                
                {formData.gerar_parcelas && (
                  <div className="flex items-center gap-2 ml-6">
                    <Checkbox 
                      id="primeira_parcela_inicio" 
                      checked={formData.primeira_parcela_inicio}
                      onCheckedChange={(checked) => setFormData({ ...formData, primeira_parcela_inicio: checked })}
                      data-testid="contract-primeira-check"
                    />
                    <Label htmlFor="primeira_parcela_inicio" className="font-normal cursor-pointer">
                      Primeira parcela na data de início
                    </Label>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Resumo do Contrato</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Número de parcelas:</span>
                    <span className="ml-2 font-medium">{numParcelas}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Valor total:</span>
                    <span className="ml-2 font-medium text-green-600">{formatCurrency(valorTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !formData.client_id || !formData.vehicle_id} 
                data-testid="generate-contract-button"
              >
                {submitting ? 'Gerando...' : 'Gerar Contrato'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Contrato</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-4 border-b">
                <div>
                  <p className="font-mono text-lg font-bold text-blue-600">{selectedContract.vehicle_id}</p>
                  <p className="text-sm text-gray-500">{selectedContract.contract_id}</p>
                </div>
                <Badge className={`${statusColors[selectedContract.status]} text-white`}>
                  {statusLabels[selectedContract.status]}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="col-span-2">
                  <p className="text-gray-500 flex items-center gap-1"><User className="w-3 h-3" /> Cliente</p>
                  <p className="font-medium">{selectedContract.client_name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 flex items-center gap-1"><Car className="w-3 h-3" /> Veículo</p>
                  <p className="font-medium">{selectedContract.vehicle_info}</p>
                </div>
                <div>
                  <p className="text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Data Início</p>
                  <p className="font-medium">{formatDate(selectedContract.data_inicio)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Data Final</p>
                  <p className="font-medium">{formatDate(selectedContract.data_final)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Tipo de Cobrança</p>
                  <p className="font-medium capitalize">{selectedContract.tipo_cobranca}</p>
                </div>
                <div>
                  <p className="text-gray-500">Duração</p>
                  <p className="font-medium">{selectedContract.duracao_meses} meses</p>
                </div>
                <div>
                  <p className="text-gray-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Valor Parcela</p>
                  <p className="font-semibold text-green-600">{formatCurrency(selectedContract.valor_parcela)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Valor Total</p>
                  <p className="font-semibold text-green-600">{formatCurrency(selectedContract.valor_total)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Multa Diária</p>
                  <p className="font-medium">{formatCurrency(selectedContract.multa_diaria)}</p>
                </div>
              </div>
              
              {selectedContract.status === 'ativo' && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowViewModal(false);
                      openCancelDialog(selectedContract);
                    }}
                    className="text-red-600"
                  >
                    Cancelar Contrato
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowViewModal(false);
                      openEndDialog(selectedContract);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Encerrar Contrato
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* End Contract Dialog */}
      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar Contrato</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja encerrar o contrato do veículo {selectedContract?.vehicle_id}? O veículo será liberado para novas locações.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEndContract}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-end-contract"
            >
              Encerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Contract Dialog */}
      {/* Add Invoice Modal */}
      <Dialog open={showAddInvoiceModal} onOpenChange={setShowAddInvoiceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Parcela Avulsa</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <form onSubmit={handleAddInvoice} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Contrato:</strong> {selectedContract.vehicle_info}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Cliente:</strong> {selectedContract.client_name}
                </p>
              </div>

              <div>
                <Label htmlFor="descricao">Descrição da Parcela *</Label>
                <Input
                  id="descricao"
                  placeholder="Ex: Caução, Taxa Extra, Manutenção..."
                  value={invoiceFormData.descricao}
                  onChange={(e) => setInvoiceFormData({ ...invoiceFormData, descricao: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="valor">Valor (R$) *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={invoiceFormData.valor_original}
                  onChange={(e) => setInvoiceFormData({ ...invoiceFormData, valor_original: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="vencimento">Data de Vencimento *</Label>
                <Input
                  id="vencimento"
                  type="date"
                  value={invoiceFormData.vencimento}
                  onChange={(e) => setInvoiceFormData({ ...invoiceFormData, vencimento: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Input
                  id="observacoes"
                  placeholder="Informações adicionais..."
                  value={invoiceFormData.observacoes}
                  onChange={(e) => setInvoiceFormData({ ...invoiceFormData, observacoes: e.target.value })}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddInvoiceModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                  {submitting ? 'Adicionando...' : 'Adicionar Parcela'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Contrato</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar o contrato do veículo {selectedContract?.vehicle_id}? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelContract}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-contract"
            >
              Cancelar Contrato
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
